import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { MessageCircle, X, Send, Loader2, ChevronDown, Mail, Sparkles, Phone, MessageSquare, FileText, Check } from "lucide-react";

const BASE = import.meta.env.BASE_URL;

type ProposalType = "request_call" | "request_followup" | "request_document";

interface ActionProposal {
  idx: number;
  type: ProposalType;
  companyId: number | null;
  reason: string;
  suggestedTitle: string;
}

interface ChatMessage {
  id?: number;
  role: "user" | "assistant";
  content: string;
  agentSource?: string | null;
  isStreaming?: boolean;
  options?: string[];
  proposals?: ActionProposal[];
  acceptedProposalIdxs?: number[];
  declinedProposalIdxs?: number[];
}

// Queue-depth preview for the Accept-card friction (S6). Cached per
// (chatMessageId, proposalIdx) so we don't re-fetch on every render.
interface QueuePreview {
  advisorName: string | null;
  openCount: number | null;
  scope: "company" | "advisor" | "unowned";
}

function parseOptions(content: string): { cleanContent: string; options: string[] } {
  const match = content.match(/\n?\[OPTIONS:\s*([^\]]+)\]\s*$/i);
  if (!match) return { cleanContent: content, options: [] };
  const options = match[1].split("|").map(s => s.trim()).filter(Boolean).slice(0, 4);
  const cleanContent = content.replace(/\n?\[OPTIONS:\s*[^\]]+\]\s*$/i, "").trim();
  return { cleanContent, options };
}

// Parse [ACTION_PROPOSAL: type=...; companyId=...; reason="..."; suggestedTitle="..."]
// tokens out of an assistant message. Tokens are emitted by Kavya as her only
// real execution lever — see kavya.ts ACTION PROPOSAL PROTOCOL section.
const PROPOSAL_TOKEN_RE = /\[ACTION_PROPOSAL:\s*([^\]]+?)\]/gi;
function parseProposals(content: string): { cleanContent: string; proposals: ActionProposal[] } {
  const proposals: ActionProposal[] = [];
  let idx = 0;
  for (const match of content.matchAll(PROPOSAL_TOKEN_RE)) {
    const body = match[1];
    const fields: Record<string, string> = {};
    // Split on `;` but keep quoted values intact.
    const parts = body.match(/(\w+)\s*=\s*("(?:[^"\\]|\\.)*"|[^;]+)/g) || [];
    for (const p of parts) {
      const eq = p.indexOf("=");
      if (eq < 0) continue;
      const key = p.slice(0, eq).trim();
      let val = p.slice(eq + 1).trim();
      if (val.startsWith("\"") && val.endsWith("\"")) {
        val = val.slice(1, -1).replace(/\\"/g, "\"").replace(/\\\\/g, "\\");
      }
      fields[key] = val;
    }
    const type = fields.type as ProposalType;
    if (type !== "request_call" && type !== "request_followup" && type !== "request_document") continue;
    const reason = (fields.reason || "").trim();
    const suggestedTitle = (fields.suggestedTitle || fields.title || "").trim();
    if (!reason || !suggestedTitle) continue;
    let companyId: number | null = null;
    if (fields.companyId && fields.companyId.toLowerCase() !== "null") {
      const n = parseInt(fields.companyId, 10);
      if (Number.isFinite(n)) companyId = n;
    }
    proposals.push({ idx: idx++, type, companyId, reason, suggestedTitle });
    if (proposals.length >= 2) break; // server-side cap mirrored
  }
  // Strip the tokens (and any partial trailing token mid-stream) from display.
  let cleanContent = content.replace(PROPOSAL_TOKEN_RE, "");
  cleanContent = cleanContent.replace(/\[ACTION_PROPOSAL:[^\]]*$/i, "");
  return { cleanContent: cleanContent.trim(), proposals };
}

function stripOptionsForDisplay(content: string): string {
  let s = content.replace(/\n?\[OPTIONS:\s*[^\]]+\]\s*$/i, "");
  s = s.replace(/\n?\[OPTIONS:[^\]]*$/i, "");
  // Hide proposal tokens (complete and partial) during streaming. The cards
  // render below the bubble once the message is fully parsed.
  s = s.replace(PROPOSAL_TOKEN_RE, "");
  s = s.replace(/\[ACTION_PROPOSAL:[^\]]*$/i, "");
  return s.trim();
}

function agentBadge(source: string | null | undefined) {
  if (!source) return null;
  const agents = source.split("+").filter(Boolean);
  const colors: Record<string, string> = {
    kavya: "bg-[#FF5C00]/10 text-[#FF5C00] border-[#FF5C00]/20",
    meera: "bg-emerald-500/10 text-emerald-700 border-emerald-500/20",
    riya: "bg-purple-500/10 text-purple-700 border-purple-500/20",
    priya: "bg-rose-500/10 text-rose-700 border-rose-500/20",
  };
  return (
    <div className="flex items-center gap-1 mt-1.5">
      {agents.map((a) => (
        <span
          key={a}
          className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full border ${colors[a] || "bg-white/10 text-white/50 border-white/10"}`}
        >
          {a.charAt(0).toUpperCase() + a.slice(1)}
        </span>
      ))}
    </div>
  );
}

// Accept-card actions with queue-depth friction (S6). Pulled into its own
// component so we can fire the preview fetch from a useEffect on mount —
// inlining it would re-fetch on every parent render of the message list.
function ProposalActions({
  msgId,
  proposal,
  preview,
  fetchPreview,
  onAccept,
  onDecline,
}: {
  msgId: number | undefined;
  proposal: ActionProposal;
  preview: QueuePreview | "loading" | "error" | undefined;
  fetchPreview: (msgId: number, idx: number) => void;
  onAccept: () => void;
  onDecline: () => void;
}) {
  useEffect(() => {
    if (msgId && preview === undefined) fetchPreview(msgId, proposal.idx);
  }, [msgId, proposal.idx, preview, fetchPreview]);

  let line: React.ReactNode = null;
  if (preview && preview !== "loading" && preview !== "error") {
    const { advisorName, openCount, scope } = preview;
    if (openCount === null || advisorName === null) {
      line = "No advisor is assigned yet — this would land in the unowned queue.";
    } else if (openCount === 0) {
      line = `${advisorName}'s ${scope === "company" ? "queue for this company" : "queue"} is empty — this would be the first.`;
    } else {
      const tone = openCount >= 10 ? "text-amber-700" : "text-[#1A1A1A]/55";
      line = (
        <span className={tone}>
          {advisorName} has {openCount} open action{openCount === 1 ? "" : "s"}
          {scope === "company" ? " for this company" : ""} — this would be #{openCount + 1}.
        </span>
      );
    }
  } else if (preview === "loading") {
    line = <span className="text-[#1A1A1A]/35">Checking your advisor's queue…</span>;
  }

  return (
    <div className="mt-2.5">
      {line && <div className="text-[10px] mb-1.5">{line}</div>}
      <div className="flex items-center gap-2">
        <button
          onClick={onAccept}
          disabled={!msgId}
          className="text-[11px] font-medium text-white bg-[#FF5C00] hover:bg-[#E55200] disabled:opacity-40 rounded-md px-2.5 py-1 transition-colors"
        >
          Accept
        </button>
        <button
          onClick={onDecline}
          disabled={!msgId}
          className="text-[11px] font-medium text-[#1A1A1A]/60 hover:text-[#1A1A1A] disabled:opacity-40 rounded-md px-2.5 py-1 transition-colors"
        >
          Decline
        </button>
        {!msgId && <span className="text-[9px] text-[#1A1A1A]/30 italic">Saving…</span>}
      </div>
    </div>
  );
}

export default function InvestorChat({ externalOpen, onClose, initialPrompt, hideLauncher = false }: { externalOpen?: boolean; onClose?: () => void; initialPrompt?: string | null; hideLauncher?: boolean } = {}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = internalOpen || (externalOpen ?? false);
  const setOpen = (val: boolean) => {
    setInternalOpen(val);
    if (!val && onClose) onClose();
  };
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [sendingSummary, setSendingSummary] = useState(false);
  const [summaryStatus, setSummaryStatus] = useState<string | null>(null);
  const [didYouKnowFacts, setDidYouKnowFacts] = useState<string[]>([]);
  const [currentFactIdx, setCurrentFactIdx] = useState(0);
  // Queue-depth previews for active proposals (S6). Keyed `${msgId}:${idx}`.
  // We fetch lazily per pending card, never for accepted/declined ones.
  const [queuePreviews, setQueuePreviews] = useState<Record<string, QueuePreview | "loading" | "error">>({});

  const fetchQueuePreview = useCallback(async (msgId: number, idx: number) => {
    const key = `${msgId}:${idx}`;
    setQueuePreviews(prev => prev[key] ? prev : { ...prev, [key]: "loading" });
    const token = localStorage.getItem("investor_token");
    if (!token) return;
    try {
      const r = await fetch(
        `${BASE}api/investor-portal/action-proposals/preview-queue?chatMessageId=${msgId}&proposalIdx=${idx}`,
        { headers: { "x-investor-token": token } },
      );
      if (!r.ok) { setQueuePreviews(prev => ({ ...prev, [key]: "error" })); return; }
      const data = await r.json();
      setQueuePreviews(prev => ({ ...prev, [key]: data as QueuePreview }));
    } catch {
      setQueuePreviews(prev => ({ ...prev, [key]: "error" }));
    }
  }, []);

  const handleSendSummary = async () => {
    const token = localStorage.getItem("investor_token");
    if (!token) return;

    setSendingSummary(true);
    setSummaryStatus(null);

    try {
      const res = await fetch(`${BASE}api/investor-portal/chat/send-summary`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-investor-token": token },
      });
      if (res.ok) {
        setSummaryStatus("Digest sent to your email!");
      } else {
        const err = await res.json();
        setSummaryStatus(err.error || "Could not send summary");
      }
    } catch {
      setSummaryStatus("Failed to send. Try again later.");
    }
    setSendingSummary(false);
    setTimeout(() => setSummaryStatus(null), 4000);
  };
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const sendingRef = useRef(false);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // When opened with a pre-filled prompt (e.g. from a dashboard suggestion),
  // populate the input box and focus it so the investor can edit or send.
  useEffect(() => {
    if (open && initialPrompt) {
      setInput(initialPrompt);
      setTimeout(() => {
        inputRef.current?.focus();
        const el = inputRef.current;
        if (el) el.setSelectionRange(el.value.length, el.value.length);
      }, 150);
    }
  }, [open, initialPrompt]);

  const loadHistory = useCallback(async () => {
    const token = localStorage.getItem("investor_token");
    if (!token) return;

    setLoadingHistory(true);
    try {
      const res = await fetch(`${BASE}api/investor-portal/chat/history`, {
        headers: { "x-investor-token": token },
      });
      if (res.ok) {
        const data = await res.json();
        const mapped = data.map((m: any) => {
          if (m.role === "assistant") {
            const stripped = parseProposals(m.content || "");
            const { cleanContent, options } = parseOptions(stripped.cleanContent);
            return {
              id: m.id,
              role: m.role,
              content: cleanContent,
              agentSource: m.agentSource,
              options: options.length > 0 ? options : undefined,
              proposals: stripped.proposals.length > 0 ? stripped.proposals : undefined,
              acceptedProposalIdxs: Array.isArray(m.acceptedProposalIdxs) ? m.acceptedProposalIdxs : [],
              declinedProposalIdxs: [],
            };
          }
          return { id: m.id, role: m.role, content: m.content, agentSource: m.agentSource };
        });
        setMessages(mapped);
      }
    } catch (err) {
      console.error("Failed to load chat history:", err);
    }
    setLoadingHistory(false);
  }, []);

  const loadFacts = useCallback(async () => {
    const token = localStorage.getItem("investor_token");
    if (!token) return;
    try {
      const res = await fetch(`${BASE}api/investor-portal/chat/did-you-know`, {
        headers: { "x-investor-token": token },
      });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data.facts)) setDidYouKnowFacts(data.facts);
      }
    } catch (err) {
      console.error("Failed to load facts:", err);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadHistory();
      loadFacts();
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open, loadHistory, loadFacts]);

  // Mix did-you-know facts with agent intros so the wait state always has
  // something meaningful to show — never an empty "..."
  const AGENT_INTROS = useMemo(() => ([
    { agent: "Kavya", body: "I'm your day-to-day concierge — I remember every conversation, every preference, and connect you to the right specialist when you need depth." },
    { agent: "Meera", body: "Meera is the platform's project manager. She synthesises signals across founders, KPIs, and your portfolio so the answer you get is grounded, not guessed." },
    { agent: "Riya", body: "Riya scans media coverage every night — Shubh Labh clips, press, founder mentions — so your portfolio's public footprint stays current." },
    { agent: "Priya", body: "Priya watches for ROFR opportunities and pricing signals across the cohort, surfacing them to you before the window closes." },
    { agent: "Viren", body: "Viren is the quality manager — he escalates stale tasks, dark companies, and over-reactions so nothing important gets quietly buried." },
    { agent: "Arjun", body: "Arjun reasons about platform-wide patterns each morning and writes the day's product brief — he's how Favcy itself learns from you." },
  ]), []);

  // Interleave: [fact, intro, fact, intro, ...] — facts first if we have them.
  const ticker = useMemo(() => {
    const intros = AGENT_INTROS;
    if (didYouKnowFacts.length === 0) {
      return intros.map(i => ({ kind: "intro" as const, agent: i.agent, body: i.body }));
    }
    const out: Array<{ kind: "fact" | "intro"; agent?: string; body: string }> = [];
    const max = Math.max(didYouKnowFacts.length, intros.length);
    for (let i = 0; i < max; i++) {
      if (i < didYouKnowFacts.length) out.push({ kind: "fact", body: didYouKnowFacts[i] });
      if (i < intros.length) out.push({ kind: "intro", agent: intros[i].agent, body: intros[i].body });
    }
    return out;
  }, [didYouKnowFacts, AGENT_INTROS]);

  useEffect(() => {
    if (!sending || ticker.length === 0) return;
    setCurrentFactIdx(0);
    const interval = setInterval(() => {
      setCurrentFactIdx(idx => (idx + 1) % ticker.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [sending, ticker.length]);

  const handleQuickReply = (option: string) => {
    if (sendingRef.current) return;
    setMessages(prev => prev.map((m, i) => i === prev.length - 1 ? { ...m, options: undefined } : m));
    sendMessageWithText(option);
  };

  const sendMessage = async () => {
    const text = input.trim();
    await sendMessageWithText(text);
  };

  const sendMessageWithText = async (rawText: string) => {
    const text = rawText.trim();
    if (!text) return;
    if (sendingRef.current) return;
    sendingRef.current = true;

    const token = localStorage.getItem("investor_token");
    if (!token) { sendingRef.current = false; return; }

    setInput("");
    setSending(true);

    const userMsg: ChatMessage = { role: "user", content: text };
    setMessages((prev) => [...prev, userMsg]);

    const assistantMsg: ChatMessage = { role: "assistant", content: "", isStreaming: true };
    setMessages((prev) => [...prev, assistantMsg]);

    try {
      const res = await fetch(`${BASE}api/investor-portal/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-investor-token": token,
        },
        body: JSON.stringify({
          message: text,
          // When the investor is reading a curated deal, the URL is
          // /investor/portal/curated/:slug. Passing the slug lets Kavya load
          // the admin-approved deal record into her context (PRD § 8). On any
          // other page this is undefined and Kavya behaves as before.
          curatedDealSlug: (() => {
            const m = window.location.pathname.match(/\/investor\/portal\/curated\/([^\/?#]+)/);
            return m ? decodeURIComponent(m[1]) : undefined;
          })(),
        }),
      });

      if (!res.ok) {
        setMessages((prev) => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last.isStreaming) {
            last.content = "I'm having trouble connecting right now. Please try again.";
            last.isStreaming = false;
          }
          return updated;
        });
        setSending(false);
        return;
      }

      const reader = res.body?.getReader();
      const decoder = new TextDecoder();

      if (reader) {
        let buffer = "";
        const processLine = (line: string) => {
          if (!line.startsWith("data: ")) return;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.content) {
              setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last.isStreaming) last.content += data.content;
                return [...updated];
              });
            }
            // The final SSE frame ({done, messageId}) can land in the tail
            // buffer after the read loop ends. We must wire messageId here
            // too — otherwise proposal cards stay disabled until a history
            // reload (architect-flagged edge case).
            if (data.done) {
              setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last && last.role === "assistant" && typeof data.messageId === "number") {
                  last.id = data.messageId;
                }
                return [...updated];
              });
            }
          } catch {}
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            if (buffer.length > 0) {
              const tail = buffer.split("\n");
              for (const l of tail) processLine(l);
              buffer = "";
            }
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              try {
                const data = JSON.parse(line.slice(6));
                if (data.content) {
                  setMessages((prev) => {
                    const updated = [...prev];
                    const last = updated[updated.length - 1];
                    if (last.isStreaming) {
                      last.content += data.content;
                    }
                    return [...updated];
                  });
                }
                if (data.done) {
                  setMessages((prev) => {
                    const updated = [...prev];
                    const last = updated[updated.length - 1];
                    if (last.isStreaming) {
                      last.isStreaming = false;
                      if (typeof data.messageId === "number") last.id = data.messageId;
                      const stripped = parseProposals(last.content);
                      const { cleanContent, options } = parseOptions(stripped.cleanContent);
                      last.content = cleanContent;
                      if (options.length > 0) last.options = options;
                      if (stripped.proposals.length > 0) {
                        last.proposals = stripped.proposals;
                        last.acceptedProposalIdxs = [];
                        last.declinedProposalIdxs = [];
                      }
                      const lower = cleanContent.toLowerCase();
                      const sources = ["kavya"];
                      if (lower.includes("meera")) sources.push("meera");
                      if (lower.includes("riya")) sources.push("riya");
                      if (lower.includes("priya")) sources.push("priya");
                      last.agentSource = sources.join("+");
                    }
                    return [...updated];
                  });
                }
                if (data.error) {
                  setMessages((prev) => {
                    const updated = [...prev];
                    const last = updated[updated.length - 1];
                    if (last.isStreaming) {
                      last.content = data.error;
                      last.isStreaming = false;
                    }
                    return [...updated];
                  });
                }
              } catch {}
            }
          }
        }
        setMessages((prev) => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last && last.role === "assistant" && last.isStreaming) {
            last.isStreaming = false;
            const stripped = parseProposals(last.content);
            const { cleanContent, options } = parseOptions(stripped.cleanContent);
            last.content = cleanContent;
            if (options.length > 0) last.options = options;
            if (stripped.proposals.length > 0 && !last.proposals) {
              last.proposals = stripped.proposals;
              last.acceptedProposalIdxs = [];
              last.declinedProposalIdxs = [];
            }
            if (!last.agentSource) {
              const lower = cleanContent.toLowerCase();
              const sources = ["kavya"];
              if (lower.includes("meera")) sources.push("meera");
              if (lower.includes("riya")) sources.push("riya");
              if (lower.includes("priya")) sources.push("priya");
              last.agentSource = sources.join("+");
            }
          }
          return updated;
        });
      }
    } catch (err) {
      console.error("Chat send error:", err);
      setMessages((prev) => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        if (last.isStreaming) {
          last.content = "Connection lost. Please try again.";
          last.isStreaming = false;
        }
        return updated;
      });
    }

    setSending(false);
    sendingRef.current = false;
  };

  const handleAcceptProposal = async (msgIdx: number, proposal: ActionProposal) => {
    const msg = messages[msgIdx];
    if (!msg || !msg.id) return;
    const token = localStorage.getItem("investor_token");
    if (!token) return;
    // Optimistic accept — locks the card immediately so the investor can't
    // double-click into a duplicate task. Reverted on server failure.
    setMessages(prev => prev.map((m, i) => i === msgIdx
      ? { ...m, acceptedProposalIdxs: [...(m.acceptedProposalIdxs || []), proposal.idx] }
      : m));
    try {
      const res = await fetch(`${BASE}api/investor-portal/action-proposals/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-investor-token": token },
        body: JSON.stringify({
          chatMessageId: msg.id,
          proposalIdx: proposal.idx,
          type: proposal.type,
          companyId: proposal.companyId,
          suggestedTitle: proposal.suggestedTitle,
          reason: proposal.reason,
        }),
      });
      if (!res.ok) {
        setMessages(prev => prev.map((m, i) => i === msgIdx
          ? { ...m, acceptedProposalIdxs: (m.acceptedProposalIdxs || []).filter(x => x !== proposal.idx) }
          : m));
      }
    } catch {
      setMessages(prev => prev.map((m, i) => i === msgIdx
        ? { ...m, acceptedProposalIdxs: (m.acceptedProposalIdxs || []).filter(x => x !== proposal.idx) }
        : m));
    }
  };

  const handleDeclineProposal = async (msgIdx: number, proposal: ActionProposal) => {
    const msg = messages[msgIdx];
    if (!msg || !msg.id) return;
    const token = localStorage.getItem("investor_token");
    if (!token) return;
    setMessages(prev => prev.map((m, i) => i === msgIdx
      ? { ...m, declinedProposalIdxs: [...(m.declinedProposalIdxs || []), proposal.idx] }
      : m));
    try {
      await fetch(`${BASE}api/investor-portal/action-proposals/decline`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-investor-token": token },
        body: JSON.stringify({
          chatMessageId: msg.id,
          proposalIdx: proposal.idx,
          suggestedTitle: proposal.suggestedTitle,
        }),
      });
    } catch {}
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!open) {
    if (hideLauncher) return null;
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-[#FF5C00] to-[#E55200] text-white shadow-lg shadow-[#FF5C00]/25 flex items-center justify-center hover:scale-105 transition-transform"
      >
        <div className="relative">
          <MessageCircle className="w-6 h-6" />
          <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[#FFD4B8] flex items-center justify-center">
            <span className="text-[6px] font-bold text-[#8B3200]">K</span>
          </div>
        </div>
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 w-[400px] h-[560px] bg-[#FFF8F2] border border-[#FF5C00]/15 rounded-2xl shadow-2xl shadow-[#FF5C00]/10 flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-[#FF5C00]/10 bg-[#FFF3EA]">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#FF5C00]/10 border border-[#FF5C00]/20 flex items-center justify-center">
            <span className="text-[#FF5C00] font-serif font-bold text-sm">K</span>
          </div>
          <div>
            <p className="text-[#1A1A1A] text-sm font-medium">Kavya</p>
            <p className="text-[#1A1A1A]/40 text-[10px]">Communications Agent</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          <span className="text-[10px] text-[#1A1A1A]/30 mr-2">Online</span>
          <button
            onClick={handleSendSummary}
            disabled={sendingSummary || messages.length === 0}
            className="text-[#1A1A1A]/30 hover:text-[#FF5C00] disabled:opacity-30 p-1 transition-colors"
            title="Email today's digest"
          >
            {sendingSummary ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
          </button>
          <button onClick={() => setOpen(false)} className="text-[#1A1A1A]/30 hover:text-[#1A1A1A]/60 p-1">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {summaryStatus && (
        <div className={`px-4 py-2 text-xs text-center ${summaryStatus.includes("sent") ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
          {summaryStatus}
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {loadingHistory ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 text-[#FF5C00]/30 animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-12 h-12 rounded-full bg-[#FF5C00]/10 border border-[#FF5C00]/15 flex items-center justify-center mx-auto mb-3">
              <span className="text-[#FF5C00] font-serif font-bold text-lg">K</span>
            </div>
            <p className="text-[#1A1A1A]/80 text-sm font-medium">Hi! I'm Kavya.</p>
            <p className="text-[#1A1A1A]/40 text-xs mt-1 max-w-[260px] mx-auto leading-relaxed">
              Your communications point of contact on Favcy Select. Ask me about your portfolio, company updates, ROFR opportunities, or anything about how the platform works.
            </p>
            <div className="mt-4 space-y-1.5">
              {[
                "How are my portfolio companies doing?",
                "Tell me about Favcy Select",
                "Any ROFR opportunities right now?",
              ].map((q) => (
                <button
                  key={q}
                  onClick={() => { setInput(q); }}
                  className="block w-full text-left text-xs text-[#1A1A1A]/50 hover:text-[#1A1A1A]/80 bg-[#FF5C00]/[0.04] hover:bg-[#FF5C00]/[0.08] border border-[#FF5C00]/10 rounded-lg px-3 py-2 transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] ${msg.role === "user" ? "order-1" : "order-0"}`}>
                {msg.role === "assistant" && (
                  <div className="flex items-center gap-1.5 mb-1">
                    <div className="w-5 h-5 rounded-full bg-[#FF5C00]/10 border border-[#FF5C00]/15 flex items-center justify-center">
                      <span className="text-[#FF5C00] font-serif font-bold text-[9px]">K</span>
                    </div>
                    <span className="text-[10px] text-[#1A1A1A]/35">Kavya</span>
                  </div>
                )}
                <div
                  className={`rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                    msg.role === "user"
                      ? "bg-[#FF5C00] text-white rounded-br-sm"
                      : "bg-white/60 text-[#1A1A1A]/80 rounded-bl-sm border border-[#FF5C00]/[0.08]"
                  }`}
                >
                  {(msg.role === "assistant" ? stripOptionsForDisplay(msg.content) : msg.content) || (msg.isStreaming && (
                    <div className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#FF5C00] animate-pulse" />
                      <div className="w-1.5 h-1.5 rounded-full bg-[#FF5C00] animate-pulse" style={{ animationDelay: "0.2s" }} />
                      <div className="w-1.5 h-1.5 rounded-full bg-[#FF5C00] animate-pulse" style={{ animationDelay: "0.4s" }} />
                    </div>
                  ))}
                  {msg.isStreaming && msg.content && (
                    <span className="inline-block w-1 h-4 bg-[#FF5C00] animate-pulse ml-0.5 align-middle" />
                  )}
                </div>
                {msg.role === "assistant" && !msg.isStreaming && agentBadge(msg.agentSource)}
                {msg.role === "assistant" && !msg.isStreaming && msg.proposals && msg.proposals.length > 0 && (
                  <div className="mt-2 space-y-2">
                    {msg.proposals.map((p) => {
                      const accepted = (msg.acceptedProposalIdxs || []).includes(p.idx);
                      const declined = (msg.declinedProposalIdxs || []).includes(p.idx);
                      const Icon = p.type === "request_call" ? Phone : p.type === "request_followup" ? MessageSquare : FileText;
                      const typeLabel = p.type === "request_call" ? "Schedule a call" : p.type === "request_followup" ? "Founder follow-up" : "Document request";
                      return (
                        <div
                          key={p.idx}
                          className={`rounded-xl border p-3 transition-colors ${
                            accepted
                              ? "border-emerald-500/30 bg-emerald-50/60"
                              : declined
                              ? "border-[#1A1A1A]/10 bg-[#1A1A1A]/[0.02] opacity-60"
                              : "border-[#FF5C00]/25 bg-[#FF5C00]/[0.04]"
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                              accepted ? "bg-emerald-500/15 text-emerald-700" : "bg-[#FF5C00]/10 text-[#FF5C00]"
                            }`}>
                              <Icon className="w-3.5 h-3.5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-[10px] font-semibold uppercase tracking-wider ${
                                accepted ? "text-emerald-700" : "text-[#FF5C00]"
                              }`}>
                                {typeLabel}
                              </p>
                              <p className="text-xs text-[#1A1A1A]/80 mt-1 leading-snug">{p.suggestedTitle}</p>
                              <p className="text-[10px] text-[#1A1A1A]/50 mt-1 leading-snug">{p.reason}</p>
                            </div>
                          </div>
                          {accepted ? (
                            <div className="mt-2 flex items-center gap-1.5 text-[10px] text-emerald-700 font-medium">
                              <Check className="w-3 h-3" />
                              Sent to your advisor's queue
                            </div>
                          ) : declined ? (
                            <div className="mt-2 text-[10px] text-[#1A1A1A]/40">Declined — no action taken.</div>
                          ) : (
                            <ProposalActions
                              msgId={msg.id}
                              proposal={p}
                              preview={msg.id ? queuePreviews[`${msg.id}:${p.idx}`] : undefined}
                              fetchPreview={fetchQueuePreview}
                              onAccept={() => handleAcceptProposal(i, p)}
                              onDecline={() => handleDeclineProposal(i, p)}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {msg.role === "assistant" && !msg.isStreaming && msg.options && msg.options.length > 0 && i === messages.length - 1 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {msg.options.map((opt) => (
                      <button
                        key={opt}
                        onClick={() => handleQuickReply(opt)}
                        disabled={sending}
                        className="text-xs text-[#FF5C00] bg-[#FF5C00]/[0.06] hover:bg-[#FF5C00]/[0.12] border border-[#FF5C00]/25 rounded-full px-3 py-1.5 transition-colors disabled:opacity-40"
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
        {sending && (() => {
          const item = ticker[currentFactIdx];
          const isIntro = item?.kind === "intro";
          const label = isIntro
            ? `${item.agent} · meet your team`
            : (item?.kind === "fact" ? "Did you know" : "Kavya is at work");
          return (
            <div className="flex justify-start">
              <div className="w-full bg-gradient-to-br from-violet-50 to-violet-50/30 border border-violet-200/60 rounded-xl px-3.5 py-3">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-violet-500 opacity-60" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-violet-600" />
                  </span>
                  <span className="text-[10px] font-semibold text-violet-700 uppercase tracking-wider">
                    Kavya is at work
                  </span>
                  <span className="text-[10px] text-violet-400">·</span>
                  <span className="text-[10px] font-semibold text-violet-500 uppercase tracking-wider flex items-center gap-1">
                    <Sparkles className="w-2.5 h-2.5" />
                    {label}
                  </span>
                </div>
                <p key={currentFactIdx} className="text-xs text-[#1A1A1A]/80 leading-relaxed animate-in fade-in duration-500">
                  {item?.body || "Consulting the portfolio data..."}
                </p>
              </div>
            </div>
          );
        })()}
        <div ref={messagesEndRef} />
      </div>

      <div className="px-4 pb-4 pt-2 border-t border-[#FF5C00]/[0.08]">
        <div className="flex items-end gap-2 bg-white/70 border border-[#FF5C00]/10 rounded-xl px-3 py-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask Kavya anything..."
            rows={1}
            className="flex-1 bg-transparent text-sm text-[#1A1A1A] placeholder-[#1A1A1A]/25 resize-none focus:outline-none min-h-[20px] max-h-[80px]"
            style={{ height: "20px" }}
            onInput={(e) => {
              const target = e.target as HTMLTextAreaElement;
              target.style.height = "20px";
              target.style.height = Math.min(target.scrollHeight, 80) + "px";
            }}
          />
          <button
            onClick={sendMessage}
            disabled={!input.trim() || sending}
            className="text-[#FF5C00] hover:text-[#FF5C00]/80 disabled:text-[#1A1A1A]/15 transition-colors p-0.5 flex-shrink-0"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
        <p className="text-[9px] text-[#1A1A1A]/25 text-center mt-1.5">Kavya may consult Meera, Riya, or Priya for deeper insights</p>
      </div>
    </div>
  );
}
